import type { NarrativeForm, ValuatorData } from './schema';

export const NARRATIVE_TITLE_MIN_CHARS = 10;
export const NARRATIVE_TITLE_MAX_CHARS = 140;
export const CONTENT_BLOCK_CONTENT_MIN_CHARS = 2;
export const INPUT_VALUE_RANGES = {
  revenuePercentage: {
    min: -100,
    max: 400,
  },
  profitMarginPercentage: {
    min: 0,
    max: 100,
  },
  priceEarningsMultiple: {
    min: 0,
    recommendedMax: 500,
  },
  discountRatePercentage: {
    min: 3,
    max: 30,
  },
  sharesPercentage: {
    min: 0.07,
    max: 0.3,
  },
};

export type ValidationErrors<T extends Record<string, unknown>> = Partial<
  Record<keyof T, string | undefined>
>;

export const validateTitle = (title: string) => {
  if (
    title.length < NARRATIVE_TITLE_MIN_CHARS ||
    title.length > NARRATIVE_TITLE_MAX_CHARS
  )
    return `Please enter a title between ${NARRATIVE_TITLE_MIN_CHARS} and ${NARRATIVE_TITLE_MAX_CHARS} characters`;
};

export const validateIsBetween = (
  value: number,
  min: number,
  max: number,
  fieldName: string
) => {
  if (value < min || value > max)
    return `${fieldName} must be a number between ${min} and ${max}.`;
};

export const validateIsGte = (
  value: number,
  min: number,
  fieldName: string
) => {
  if (value < min || !isFinite(value) || value > Number.MAX_SAFE_INTEGER)
    return `${fieldName} must be greater than or equal to ${min}.`;
};

export const validateValuatorData = ({
  revenuePercentage,
  discountRatePercentage,
  priceEarningsMultiple,
  profitMarginPercentage,
}: Pick<
  ValuatorData,
  | 'revenuePercentage'
  | 'discountRatePercentage'
  | 'priceEarningsMultiple'
  | 'profitMarginPercentage'
>) => {
  return {
    revenuePercentage: validateIsBetween(
      revenuePercentage,
      INPUT_VALUE_RANGES.revenuePercentage.min,
      INPUT_VALUE_RANGES.revenuePercentage.max,
      'Revenue'
    ),
    discountRatePercentage: validateIsBetween(
      discountRatePercentage,
      INPUT_VALUE_RANGES.discountRatePercentage.min,
      INPUT_VALUE_RANGES.discountRatePercentage.max,
      'Discount rate'
    ),
    priceEarningsMultiple: validateIsGte(
      priceEarningsMultiple,
      INPUT_VALUE_RANGES.priceEarningsMultiple.min,
      'PE ratio'
    ),
    profitMarginPercentage: validateIsBetween(
      profitMarginPercentage,
      INPUT_VALUE_RANGES.profitMarginPercentage.min,
      INPUT_VALUE_RANGES.profitMarginPercentage.max,
      'Profit margin'
    ),
  } satisfies ValidationErrors<ValuatorData>;
};

export const validateNarrativeForm = (values: NarrativeForm) => {
  return {
    ...validateValuatorData(values),
    title: validateTitle(values.title),
  };
};

export const validateDraftNarrativeForm = (_values: NarrativeForm) => {
  return {};
};
